import { useMemo } from 'react';
import { Stack } from '@optimus-web/core';
import { eqUniversal } from '@mews/fp';
import { createZodFormResolver, lt, useFormControls, useSafeUrlState } from '@mews/framework';
import {
    LedgersFiltersPanelWrapper,
    OwnerTypeFilter,
    BalanceFilter,
    ReservationStatusFilter,
    DateRangeFilter,
} from '../../common/filters';
import { customerTypesOptions } from '../../common/constants';
import { useDepositLedgerFilterContext } from '../context/DepositLedgerFilterContext';
import { filtersSchema, FiltersValueKey, DepositLedgerFilterValues, urlSchema } from '../schema';
import { AssociatedProfileTypeFilter } from '../../common/filters/AssociatedProfileTypeFilter';
import { reservationStatusOptions } from '../constants';
import { defaultDepositLedgerFilterValues as defaultValues } from './defaultDepositLedgerFilterValues';

export const DepositLedgerTableFilters = () => {
    const { setFilterValues, filterValues, resetFilterValues } = useDepositLedgerFilterContext();
    const [urlState] = useSafeUrlState(urlSchema);

    const filterPanelFiltersAreApplied = useMemo(
        () => !eqUniversal.equals(filterValues, defaultValues),
        [filterValues]
    );

    const handleSubmit = (values: DepositLedgerFilterValues) => {
        setFilterValues(values);
    };

    const { renderFormContent, submit, errors, reset } = useFormControls<DepositLedgerFilterValues>({
        id: 'deposit-filter',
        mode: 'onSubmit',
        onSubmit: handleSubmit,
        defaultValues: urlState?.filterValues ?? defaultValues,
        resolver: createZodFormResolver(filtersSchema),
    });
    const handleFilterCloseWithoutChange = () => reset({ ...filterValues });

    const resetFilters = () => {
        reset({ ...defaultValues });
        resetFilterValues(defaultValues);
    };

    return renderFormContent(
        <Stack spacing="100">
            <LedgersFiltersPanelWrapper
                title={lt('DepositLedgerFilterPanelTitle')}
                filterPanelFiltersAreApplied={filterPanelFiltersAreApplied}
                resetFilters={resetFilters}
                submit={submit}
                errors={errors}
                onClose={handleFilterCloseWithoutChange}
            >
                <OwnerTypeFilter
                    name={FiltersValueKey.ownerTypes}
                    helper={lt('LedgerFilterCustomerTypeHelperText')}
                    ownerTypesOptions={customerTypesOptions}
                />
                <AssociatedProfileTypeFilter
                    name={FiltersValueKey.associatedAccountTypes}
                    helper={lt('LedgerFilterCustomerTypeHelperText')}
                    typeOptions={customerTypesOptions}
                />
                <ReservationStatusFilter
                    name={FiltersValueKey.reservationStates}
                    reservationStatusOptions={reservationStatusOptions}
                />
                <DateRangeFilter
                    startDateName={FiltersValueKey.arrivalStart}
                    startDateLabel={lt('DepositLedgerFilterArrivalStartTitle')}
                    endDateName={FiltersValueKey.arrivalEnd}
                    endDateLabel={lt('DepositLedgerFilterArrivalEndTitle')}
                />
                <BalanceFilter balanceMin={FiltersValueKey.balanceMin} balanceMax={FiltersValueKey.balanceMax} />
            </LedgersFiltersPanelWrapper>
        </Stack>
    );
};
